import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { dedupeRequest } from "../utils/requestDedupe";
import {
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TextField,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Collapse,
  CircularProgress,
  Badge,
  Chip,
  Avatar,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Zoom,
  InputAdornment,
  Grid,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BedroomParentIcon from "@mui/icons-material/BedroomParent";
import BedIcon from "@mui/icons-material/Bed";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UpdateIcon from "@mui/icons-material/Update";
import InventoryIcon from "@mui/icons-material/Inventory";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, isToday, isSameDay, addYears, addMonths } from 'date-fns';
import useCustomToast from "../hooks/useCustomToast";
import useHotelList from "../hooks/useHotelList";
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import EventIcon from '@mui/icons-material/Event';
import CloseIcon from '@mui/icons-material/Close';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { getCurrencySymbolFromCode } from '../utils/currencyUtils';

const MAX_TABTRIPS_ROOM_COUNT = 100;

function isRoomCountOverMax(count, maxCount) {
  if (count === "" || count === null || count === undefined) return false;
  const n = parseInt(String(count).trim(), 10);
  if (Number.isNaN(n)) return false;
  return n > maxCount;
}

// Styled components
const ResponsiveContainer = styled(motion.div)(({ theme }) => ({
  padding: theme.spacing(1.5),
  maxWidth: "100%",
  overflowX: "hidden",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.75),
  },
}));



const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  maxWidth: "100%",
  margin: "auto",
  position: "relative",
  borderRadius: theme.spacing(1),
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  overflow: 'hidden',
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(0.5),
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2.5),
  flexWrap: "wrap",
  gap: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
    marginBottom: theme.spacing(2),
  },
}));

const DateNavigator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    justifyContent: "center",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  transition: "all 0.3s ease-in-out",
  marginTop: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.75),
  WebkitOverflowScrolling: "touch",
  maxWidth: "100%",
  [theme.breakpoints.down('sm')]: {
    overflowX: "hidden",
  },
  overflowX: "auto",
  "&::-webkit-scrollbar": {
    height: "6px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255, 119, 0, 0.3)",
    borderRadius: "3px",
    "&:hover": {
      backgroundColor: "rgba(255, 119, 0, 0.5)",
    }
  },
  "& table": {
    tableLayout: 'fixed',
    width: '100%',
    [theme.breakpoints.down("md")]: {
      minWidth: "650px",
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  },
}));

const AnimatedTableRow = styled(motion.create(TableRow))(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(0, 0, 0, 0.01)",
  },
  "&:hover": {
    backgroundColor: "rgba(255, 119, 0, 0.03)",
  },
}));

const StyledInput = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-input": {
    padding: theme.spacing(1),
    height: '1.4375em',
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.75),
    },
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.2s ease',
    "&.Mui-focused": {
      boxShadow: '0 0 0 3px rgba(255, 119, 0, 0.1)',
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#FF7700",
        borderWidth: '1px',
      },
    },
    "&:hover": {
      backgroundColor: 'rgba(255, 119, 0, 0.02)',
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255, 119, 0, 0.7)",
      },
    },
  },
  "& .MuiInputLabel-root": {
    color: 'rgba(0, 0, 0, 0.6)',
    "&.Mui-focused": {
      color: "#FF7700",
    },
  },
  "& .MuiInputAdornment-root": {
    "& .MuiSvgIcon-root": {
      color: 'rgba(255, 119, 0, 0.7)',
    }
  }
}));

const BulkUpdateContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    "& .MuiButton-root": {
      width: "100%",
    },
  },
}));

const ResponsiveTable = styled(Table)(({ theme }) => ({
  tableLayout: 'fixed',
  borderCollapse: 'separate',
  borderSpacing: 0,
  '& th, & td': {
    padding: theme.spacing(1.25),
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(0.75),
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.5),
    },
    textAlign: 'center',
  },
  [theme.breakpoints.up('lg')]: {
    '& th, & td': {
      fontSize: '0.9rem',
    },
  },
}));

const DateCell = styled(TableCell)(({ theme, isSelected, isToday }) => ({
  backgroundColor: isSelected 
    ? 'rgba(255, 119, 0, 0.1)' 
    : isToday 
      ? 'rgba(255, 119, 0, 0.05)'
      : 'rgba(255, 235, 215, 0.3)',
  borderBottom: isSelected 
    ? `2px solid #FF7700` 
    : isToday 
      ? `2px solid rgba(255, 119, 0, 0.5)` 
      : `1px solid rgba(0, 0, 0, 0.05)`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'rgba(255, 119, 0, 0.1)',
  },
  padding: '8px 4px',
  width: 'calc(100% / 7)', // Set a fixed width for date cells
}));

const MobileDateSelector = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.25),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1.5),
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    maxWidth: '140px',
    height: '44px',
    '&:hover': {
      backgroundColor: 'rgba(255, 119, 0, 0.05)',
    },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(255, 119, 0, 0.1)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#FF7700',
        borderWidth: '1px',
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '0.9rem',
      paddingTop: '8px',
      paddingBottom: '8px',
    },
    '& .MuiInputAdornment-root': {
      '& .MuiButtonBase-root': {
        color: '#FF7700',
      }
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
    transition: 'all 0.2s ease',
  },
}));

const RoomTypeButton = styled(Button)(({ theme }) => ({
  justifyContent: 'flex-start',
  padding: theme.spacing(0.75),
  borderRadius: theme.shape.borderRadius,
  '&[data-is-expanded="true"]': {
    backgroundColor: 'rgba(255, 119, 0, 0.1)',
    color: '#FF7700',
    fontWeight: 600,
    '& .MuiButton-endIcon': {
      color: '#FF7700',
    },
  },
  '&[data-is-expanded="false"]': {
    backgroundColor: 'transparent',
  color: theme.palette.text.primary,
    fontWeight: 500,
    '& .MuiButton-endIcon': {
      color: theme.palette.text.secondary,
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 119, 0, 0.1)',
  },
  '& .MuiButton-endIcon': {
    marginLeft: 'auto',
  },
}));

const GuestRateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 0),
  backgroundColor: 'rgba(255, 119, 0, 0.02)',
  borderRadius: theme.shape.borderRadius,
  margin: 0,
  width: '100%',
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.25, 0),
  },
}));

const GuestRateRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: 0,
  margin: 0,
  width: '100%',
  height: '40px',
  backgroundColor: 'transparent',
  [theme.breakpoints.down('sm')]: {
    height: 'auto',
    marginBottom: '4px',
  }
}));

const GuestLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: '0 4px',
  height: '100%',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  justifyContent: 'flex-start',
  width: '15%',
  [theme.breakpoints.down('sm')]: {
    width: '40%',
    fontSize: '0.8rem',
  }
}));

const GuestCountContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 119, 0, 0.1)',
  borderRadius: '12px',
  padding: '1px 6px 1px 4px',
  fontSize: '0.7rem',
  fontWeight: 'bold',
  color: '#FF7700',
  minWidth: '20px',
}));

const StyledPersonIcon = styled(PersonIcon)(({ theme }) => ({
  fontSize: '12px',
  marginRight: '1px',
  color: '#FF7700',
}));

const BulkUpdateForm = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  '& .MuiButton-root': {
    minHeight: 44,
  },
  '& .MuiCard-root': {
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 8px 24px rgba(255, 119, 0, 0.15)',
      transform: 'translateY(-2px)',
    },
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

const BulkOptionCard = styled(Card)(({ theme, isActive }) => ({
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  borderLeft: isActive ? '4px solid #FF7700' : 'none',
  backgroundColor: isActive ? 'rgba(255, 119, 0, 0.05)' : theme.palette.background.paper,
  position: 'relative',
  overflow: 'visible',
  height: '100%',
  minHeight: 110,
  display: 'flex',
  flexDirection: 'column',
  '&::before': isActive ? {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderWidth: '0 12px 12px 0',
    borderColor: 'transparent #FF7700 transparent transparent',
  } : {},
}));

const BulkOptionContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 2.5),
  flex: 1,
  '&:last-child': {
    paddingBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 2),
  },
}));

const IconWrapper = styled(Box)(({ theme, isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: isActive ? 'rgba(255, 119, 0, 0.15)' : 'rgba(0, 0, 0, 0.04)',
  color: isActive ? '#FF7700' : theme.palette.text.secondary,
  transition: 'all 0.3s ease',
}));

const StyledFormSection = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 246, 237, 0.5)',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2.5),
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03) inset',
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 2.5),
  borderRadius: theme.spacing(1),
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(255, 119, 0, 0.1)',
  backgroundColor: '#FF7700',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '&:hover': {
    backgroundColor: '#E56900',
    boxShadow: '0 6px 16px rgba(255, 119, 0, 0.25)',
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(1px)',
    boxShadow: '0 2px 8px rgba(255, 119, 0, 0.2)',
  },
  '&.MuiButton-outlined': {
    color: '#FF7700',
    borderColor: '#FF7700',
    backgroundColor: 'transparent',
  boxShadow: 'none',
  '&:hover': {
      backgroundColor: 'rgba(255, 119, 0, 0.05)',
      borderColor: '#E56900',
      boxShadow: '0 2px 8px rgba(255, 119, 0, 0.1)',
    },
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.2rem',
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: '0.875rem',
  },
}));

const SoldOutBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: '#ff3333',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.7rem',
  height: '20px',
  marginTop: '2px',
  '& .MuiChip-label': {
    padding: '0 6px',
  },
}));

const StyledArrowButton = styled(IconButton)(({ theme }) => ({
  color: '#FF7700',
  backgroundColor: 'rgba(255, 119, 0, 0.05)',
  border: '1px solid rgba(255, 119, 0, 0.2)',
  padding: 6,
  '&:hover': {
    backgroundColor: 'rgba(255, 119, 0, 0.1)',
  },
  '&.Mui-disabled': {
    color: 'rgba(0, 0, 0, 0.26)',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  }
}));

const TodayIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '0',
  right: '0',
  width: '0',
  height: '0',
  borderStyle: 'solid',
  borderWidth: '0 8px 8px 0',
  borderColor: 'transparent #FF7700 transparent transparent',
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  minHeight: '240px',
}));

// Daywise Rate Dialog Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1),
      maxWidth: 'calc(100% - 16px)',
      borderRadius: theme.spacing(1.5),
    },
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'rgba(255, 119, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 2),
  },
}));

const DialogTitleStyled = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.5rem',
  color: '#FF7700',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.25rem',
  },
}));

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const DaySelectionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: 'rgba(255, 246, 237, 0.5)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(255, 119, 0, 0.1)',
}));

const DayChip = styled(Chip)(({ theme, selected }) => ({
  backgroundColor: selected ? '#FF7700' : 'rgba(255, 119, 0, 0.1)',
  color: selected ? '#fff' : '#FF7700',
  fontWeight: 600,
  border: `1px solid ${selected ? '#FF7700' : 'rgba(255, 119, 0, 0.3)'}`,
  '&:hover': {
    backgroundColor: selected ? '#E56900' : 'rgba(255, 119, 0, 0.2)',
  },
  transition: 'all 0.2s ease',
}));

const RoomInputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(255, 119, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
  },
}));

const RoomInputRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
}));

const StyledInputField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    textAlign: 'center',
    fontWeight: 600,
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '&[type=number]': {
      MozAppearance: 'textfield',
    },
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: theme.spacing(1),
    '&:hover': {
      backgroundColor: 'rgba(255, 119, 0, 0.05)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 119, 0, 0.08)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#FF7700',
        borderWidth: '2px',
      },
    },
  },
}));

const SelectedDaysDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor: 'rgba(255, 119, 0, 0.05)',
  borderRadius: theme.spacing(0.5),
  minHeight: '32px',
}));

const EmptyDiv = styled(Box)(({ theme }) => ({
  width: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const DateHeaderCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '40px',
}));

const HeaderWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  padding: '4px 0',
  width: '100%',
  height: '40px',
  '&[data-is-today="true"]::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    borderStyle: 'solid',
    borderWidth: '0 8px 8px 0',
    borderColor: 'transparent #FF7700 transparent transparent',
  }
}));

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';
function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

const RoomRatesCalendar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [expandedRooms, setExpandedRooms] = useState({
    deluxe: false,
    superDeluxe: false,
    standard: false,
    suite: false,
  });
  const [hotelDetails, setHotelDetails] = useState(null);
  const [inventoryData, setInventoryData] = useState([]);
  const [amenitiesByCategory, setAmenitiesByCategory] = useState([]);
  const [roomTypesData, setRoomTypesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const navigate = useNavigate();
  const [bulkUpdateOption, setBulkUpdateOption] = useState("roomCount"); // Default to "Set Bulk Room Count"
  const [formData, setFormData] = useState({
    startDate: formatDate(new Date()),
    endDate: formatDate(addMonths(new Date(), 1)),
    roomTypeId: "",
    count: "",
    occupancy: "", // Dynamic occupancy
    rates: {}, // Dynamic rates
    roomCounts: {}, // roomTypeId -> count (bulk)
  });
  
  // Custom toast hooks
  const { successToast, errorToast, infoToast } = useCustomToast();
  
  // Use hotel list hook
  const { hotels } = useHotelList();

  // Extract hotelId from route params
  const { hotelId } = useParams();
  const token = getDecryptedToken();

  // Add this state near other useState declarations
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [waitingForHotelSelection, setWaitingForHotelSelection] = useState(!hotelId); // Only wait if no hotelId in URL
  
  // Daywise Rate Dialog States
  const [showDaywiseDialog, setShowDaywiseDialog] = useState(false);
  const [daywiseFormData, setDaywiseFormData] = useState({
    startDate: formatDate(new Date()),
    endDate: formatDate(addMonths(new Date(), 1)),
    selectedDays: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
    selectedRoomTypes: [], // Changed to array to support multiple room types
    roomTypes: []
  });
  const [isDaywiseSubmitting, setIsDaywiseSubmitting] = useState(false);
  const [daywiseValidationErrors, setDaywiseValidationErrors] = useState({}); // Track field-specific errors
  const [isSavingInventory, setIsSavingInventory] = useState(false);
  const dateSectionRef = useRef(null); // Ref for scrolling to date section

  const hotelCurrency = hotelDetails?.currency || 'INR';
  const currencyLabel = getCurrencySymbolFromCode(hotelCurrency);

  // Get occupancy for a room type (category_id) using amenities; fall back to rate keys
  const getOccupancyForCategory = (categoryId, fallbackRates = {}, sourceAmenities = amenitiesByCategory) => {
    const amenitiesSource = Array.isArray(sourceAmenities) ? sourceAmenities : amenitiesByCategory;
    const categoryInfo = amenitiesSource?.find(
      (cat) => parseInt(cat?.category_id, 10) === parseInt(categoryId, 10)
    );
    const occupancy = parseInt(categoryInfo?.occupancy, 10);
    if (occupancy && occupancy > 0) return occupancy;

    // Fallback: derive from rate keys
    const numericGuestCounts = Object.keys(fallbackRates || {})
      .map((key) => parseInt(key.replace('guest', ''), 10))
      .filter((num) => !Number.isNaN(num) && num > 0);
    const maxFromRates = numericGuestCounts.length > 0 ? Math.max(...numericGuestCounts) : 0;
    return maxFromRates > 0 ? maxFromRates : 1;
  };

  // Format category_name from API to display form (must match inventory processing)
  const formatCategoryDisplayName = (rawCategoryName) => {
    if (!rawCategoryName || typeof rawCategoryName !== 'string') return '';
    return rawCategoryName.replace(/_room$/, '').replace(/_/g, ' ').trim();
  };

  // Resolve roomtype_id from display name (for room types that have no inventory yet)
  const getRoomTypeIdByDisplayName = (displayName) => {
    if (!displayName || !Array.isArray(amenitiesByCategory)) return undefined;
    const normalized = String(displayName).toLowerCase().trim();
    const found = amenitiesByCategory.find(
      (cat) => formatCategoryDisplayName(cat.category_name).toLowerCase() === normalized
    );
    return found != null ? found.category_id : undefined;
  };

  // Keep only rate keys up to occupancy (preserve blanks for UI)
  const sanitizeRatesForOccupancy = (rates = {}, occupancy = 1) => {
    const cleaned = {};
    Object.entries(rates || {}).forEach(([key, val]) => {
      const guestNum = parseInt(key.replace('guest', ''), 10);
      if (Number.isNaN(guestNum) || guestNum < 1 || guestNum > occupancy) return;
      cleaned[`${guestNum}guest`] = typeof val === 'string' && val.trim() === '' ? '' : val;
    });
    return cleaned;
  };

  // Build numeric rate payload constrained to occupancy for API calls
  const buildRatePayload = (rates = {}, occupancy = 1) => {
    const payload = {};
    Object.entries(rates || {}).forEach(([key, val]) => {
      const guestNum = parseInt(key.replace('guest', ''), 10);
      if (Number.isNaN(guestNum) || guestNum < 1 || guestNum > occupancy) return;
      const parsed = parseInt(val, 10);
      if (!Number.isNaN(parsed)) {
        payload[`${guestNum}guest`] = parsed;
      }
    });
    return payload;
  };

  // Update waiting state when hotelId changes
  useEffect(() => {
    if (hotelId) {
      setWaitingForHotelSelection(false);
    }
  }, [hotelId]);

  // Handle automatic hotel selection and redirection
  useEffect(() => {
    const checkAndSetHotel = () => {
      // If we have a hotelId in the URL, we're good
      if (hotelId) {
        setWaitingForHotelSelection(false);
        return true;
      }
      
      // If no hotelId in URL, check if we have hotels available
      if (hotels && hotels.length > 0) {
        const currentHotelId = localStorage.getItem("selectedHotelId");
        
        if (currentHotelId) {
          // We have a selected hotel, redirect to it
          navigate(`/room-rates-calendar/${currentHotelId}`, { replace: true });
          return true;
        } else {
          // No hotel selected, select the first one and redirect
          const firstHotel = hotels[0];
          localStorage.setItem("selectedHotelId", firstHotel.hotel_id);
          localStorage.setItem("selectedHotelName", firstHotel.hotel_name);
          
          if (firstHotel.host_id) {
            localStorage.setItem("selectedHostId", firstHotel.host_id);
          }
          
          navigate(`/room-rates-calendar/${firstHotel.hotel_id}`, { replace: true });
          return true;
        }
      }
      
      return false;
    };

    // Check immediately
    if (checkAndSetHotel()) {
      return;
    }

    // If not ready, wait a bit and check again
    const timer = setTimeout(() => {
      if (checkAndSetHotel()) {
        return;
      }
      
      // If still not ready after 1 second, check if we have any hotels at all
      if (hotels && hotels.length === 0) {
        setWaitingForHotelSelection(false);
        errorToast("No properties available. Please add a property first.");
      } else if (!hotels) {
        // Still loading hotels, wait a bit more
        const secondTimer = setTimeout(() => {
          if (checkAndSetHotel()) {
            return;
          }
          setWaitingForHotelSelection(false);
          errorToast("Unable to load properties. Please refresh the page.");
        }, 1000);
        
        return () => clearTimeout(secondTimer);
      } else {
        setWaitingForHotelSelection(false);
        errorToast("No properties available. Please contact support.");
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [hotels, hotelId, navigate, errorToast]);

  // Listen for hotel selection changes from header or MyProperties
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedHotelId' && e.newValue) {
        const newHotelId = e.newValue;
        // Only redirect if we're not already on the correct hotel page
        if (hotelId !== newHotelId) {
          navigate(`/room-rates-calendar/${newHotelId}`, { replace: true });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [hotelId, navigate]);

  // Also listen for custom events from header component
  useEffect(() => {
    const handleHotelChange = (event) => {
      const { hotelId: newHotelId } = event.detail;
      if (newHotelId && hotelId !== newHotelId) {
        navigate(`/room-rates-calendar/${newHotelId}`, { replace: true });
      }
    };

    window.addEventListener('hotelChanged', handleHotelChange);
    return () => window.removeEventListener('hotelChanged', handleHotelChange);
  }, [hotelId, navigate]);

  useEffect(() => {
    // Don't check access control if we're still waiting for hotel selection
    if (waitingForHotelSelection) {
      return;
    }
    
    // Access control: Only allow hotels assigned to this user
    const hotelsListStr = localStorage.getItem('hotelsList');
    let allowedHotelIds = [];
    if (hotelsListStr) {
      // hotelsListStr is encrypted, so decrypt it using the same logic as elsewhere
      let hotelsList = null;
      try {
        const SECRET_KEY = 'tabtrips_hotel_secret';
        const bytes = CryptoJS.AES.decrypt(hotelsListStr, SECRET_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        hotelsList = JSON.parse(decrypted);
      } catch (e) {
        hotelsList = null;
      }
      if (Array.isArray(hotelsList)) {
        allowedHotelIds = hotelsList.map(h => String(h.hotel_id));
      }
    }
    
    if (hotelId && !allowedHotelIds.includes(String(hotelId))) {
      errorToast('You do not have access to this hotel.');
      navigate('/dashboard', { replace: true });
      return;
    }
    
    // If we have a valid hotelId but no token, show login error
    if (hotelId && !token) {
      errorToast('Please log in to view room rates.');
      navigate('/login', { replace: true });
      return;
    }
  }, [hotelId, navigate, errorToast, waitingForHotelSelection, token]);

  // Toggle room expansion
  const toggleRoomExpansion = (roomType) => {
    setExpandedRooms((prev) => ({
      ...prev,
      [roomType]: !prev[roomType],
    }));
  };

  // Generate dates for the current week
  const generateDates = (date) => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const newDate = new Date(date);
      newDate.setDate(date.getDate() + i);
      dates.push(newDate);
    }
    return dates;
  };

  const dates = generateDates(currentDate);

  // Format date as "YYYY-MM-DD" (use local date to avoid timezone shifting the day)
  function formatDate(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Format date as "YYYY-MM-DD" in local time
  function formatDateLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }


  // Fetch room types from API
  const fetchRoomTypes = async () => {
    if (!token || !hotelId) return;
    try {
      const response = await dedupeRequest(
        `getAllRoomTypes:${hotelId}`,
        () =>
          axios.post(
            "https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getAllRoomTypes",
            {
              token: token,
              hotel_id: parseInt(hotelId, 10),
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          ),
        3000
      );

      if (response.data.status) {
        setRoomTypesData(response.data.rooms_details || []);
      } else {
        console.error("Failed to fetch room types:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching room types:", error);
    }
  };

  // Fetch hotel details from API
  const fetchHotelDetails = async (last_date, type) => {
    // Don't fetch if we're still waiting for hotel selection
    if (waitingForHotelSelection || !token || !hotelId) {
      return;
    }
    
    try {
      setLoading(true);
      const dedupeKey = `getHotelDetails:${hotelId}:${last_date}:${type || 'init'}`;
      const response = await dedupeRequest(
        dedupeKey,
        () => {
          const formData = new FormData();
          formData.append("hotel_id", hotelId);
          formData.append("last_date", last_date);
          formData.append("token", token);
          if (type) formData.append("type", type);
          return axios.post(
            "https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHotelDetails",
            formData
          );
        },
        2500
      );

      if (response.data.status) {
        const { hotel_details, inventory, amenities_by_category } = response.data;
        
        // Store amenities_by_category for determining occupancy
        if (amenities_by_category) {
          setAmenitiesByCategory(amenities_by_category);
        }
        
        // Process inventory data safely
        let processedInventory = [];
        if (Array.isArray(inventory)) {
          // Deduplicate inventory data by date and roomtype_id
          const uniqueInventory = new Map();
          
          inventory.forEach((item) => {
            try {
              const key = `${item.date}-${item.roomtype_id}`;
              if (!uniqueInventory.has(key)) {
                // Check if this room type is active
                if (!isRoomTypeActive(item.roomtype_id)) {
                  return; // Skip inactive room types
                }
                
                // Safely process category_name
                let categoryName = item.category_name || 'Unknown';
                if (typeof categoryName === 'string') {
                  categoryName = categoryName.replace(/_room$/, "").replace(/_/g, " ").trim();
                }
                
                // Safely process rate data
                let rateData = {};
                if (item.rate) {
                  if (typeof item.rate === 'string') {
                    try {
                      rateData = JSON.parse(item.rate);
                    } catch (e) {
                      console.warn("Failed to parse rate JSON:", item.rate);
                      rateData = {};
                    }
                  } else if (typeof item.rate === 'object') {
                    rateData = item.rate;
                  }
                }

                // Keep rates only up to the allowed occupancy
                const occupancyForItem = getOccupancyForCategory(
                  item.roomtype_id,
                  rateData,
                  amenities_by_category
                );
                const sanitizedRate = sanitizeRatesForOccupancy(rateData, occupancyForItem);
                
                uniqueInventory.set(key, {
                  ...item,
                  category_name: categoryName,
                  rate: sanitizedRate,
                  count: parseInt(item.count) || 0
                });
              }
            } catch (itemError) {
              console.error("Error processing inventory item:", item, itemError);
            }
          });
          
          processedInventory = Array.from(uniqueInventory.values());
        }

        setHotelDetails({ ...hotel_details, inventory: processedInventory });
        setInventoryData(processedInventory);
      } else {
        console.error("API returned error:", response.data);
        errorToast(response.data.message || "Failed to fetch hotel details.");
      }
    } catch (error) {
      console.error("Error fetching hotel details:", error);
      errorToast("An error occurred while fetching hotel details.");
    } finally {
      setLoading(false);
    }
  };

  // Handle left arrow click (fetch data for previous week)
  // Send type "next" and last_date = START of the week we're now displaying (e.g. 11-2-2026 for week 11–17)
  const handleLeftArrowClick = () => {
    const previousWeekDate = new Date(currentDate);
    previousWeekDate.setDate(previousWeekDate.getDate() - 7);
    setCurrentDate(previousWeekDate);
    fetchHotelDetails(formatDate(previousWeekDate), "next");
  };

  // Handle right arrow click (fetch data for next week)
  // Send type "next" and last_date = START of the week we're now displaying (e.g. 18-2-2026 for week 18–24)
  const handleRightArrowClick = () => {
    const nextWeekDate = new Date(currentDate);
    nextWeekDate.setDate(nextWeekDate.getDate() + 7);
    setCurrentDate(nextWeekDate);
    fetchHotelDetails(formatDate(nextWeekDate), "next");
  };

  // Handle calendar icon click (fetch data for selected date)
  const handleCalendarDateChange = (selectedDate) => {
    const newDate = new Date(selectedDate);
    setCurrentDate(newDate);
    fetchHotelDetails(formatDate(newDate), "next"); // Send type: "next"
  };

  // Initial fetch on component mount
  useEffect(() => {
    // Don't fetch if we're still waiting for hotel selection
    if (waitingForHotelSelection) {
      return;
    }
    
    const lastDate = formatDate(currentDate);
    fetchRoomTypes(); // Fetch room types first
    fetchHotelDetails(lastDate); // Initial fetch without "type"
  }, [hotelId, waitingForHotelSelection]);

  // Handle input changes for room count
  const handleRoomCountChange = (date, roomType, value) => {
    const parsedValue = parseInt(value, 10);
    setInventoryData((prev) => {
      // Create a map of existing data
      const dataMap = new Map(
        prev.map(item => [`${item.date}-${item.roomtype_id}`, item])
      );

      const itemToUpdate = prev.find(
        item => item.date === date && item.category_name.toLowerCase() === roomType.toLowerCase()
      );

      if (itemToUpdate) {
        const key = `${date}-${itemToUpdate.roomtype_id}`;
        dataMap.set(key, {
          ...itemToUpdate,
          count: isNaN(parsedValue) ? 0 : parsedValue
        });
      } else {
        // Create new inventory entry for room types that have no inventory yet
        const roomTypeId = getRoomTypeIdByDisplayName(roomType);
        if (roomTypeId != null) {
          const occupancy = getOccupancyForCategory(roomTypeId, {});
          const rate = {};
          for (let i = 1; i <= occupancy; i++) rate[`${i}guest`] = '';
          const key = `${date}-${roomTypeId}`;
          dataMap.set(key, {
            date,
            roomtype_id: roomTypeId,
            category_name: roomType,
            count: isNaN(parsedValue) ? 0 : parsedValue,
            rate
          });
        }
      }

      return Array.from(dataMap.values());
    });
  };

  // Handle input changes for rates
  const handleRateChange = (date, roomType, guestKey, value) => {
    const parsedValue = parseInt(value, 10);
    setInventoryData((prev) => {
      const dataMap = new Map(
        prev.map(item => [`${item.date}-${item.roomtype_id}`, item])
      );

      const itemToUpdate = prev.find(
        item => item.date === date && item.category_name.toLowerCase() === roomType.toLowerCase()
      );

      if (itemToUpdate) {
        const key = `${date}-${itemToUpdate.roomtype_id}`;
        dataMap.set(key, {
          ...itemToUpdate,
          rate: {
            ...itemToUpdate.rate,
            [guestKey]: isNaN(parsedValue) ? 0 : parsedValue
          }
        });
      } else {
        const roomTypeId = getRoomTypeIdByDisplayName(roomType);
        if (roomTypeId != null) {
          const occupancy = getOccupancyForCategory(roomTypeId, {});
          const rate = {};
          for (let i = 1; i <= occupancy; i++) rate[`${i}guest`] = '';
          rate[guestKey] = isNaN(parsedValue) ? 0 : parsedValue;
          const key = `${date}-${roomTypeId}`;
          dataMap.set(key, {
            date,
            roomtype_id: roomTypeId,
            category_name: roomType,
            count: 0,
            rate
          });
        }
      }

      return Array.from(dataMap.values());
    });
  };

  // Save updated data to API
  const saveInventoryData = async () => {
    setIsSavingInventory(true);
    
    try {
      for (const item of inventoryData) {
        if (isRoomCountOverMax(item?.count, MAX_TABTRIPS_ROOM_COUNT)) {
          errorToast(`Room count cannot exceed ${MAX_TABTRIPS_ROOM_COUNT}. Please fix and save again.`);
          setIsSavingInventory(false);
          return;
        }
      }

      // Create a map to store unique room details by date and roomtype_id
      const uniqueRoomDetails = new Map();

      // Process inventory data to handle duplicates and null values
      inventoryData.forEach((item) => {
        const key = `${item.date}-${item.roomtype_id}`;
        
        // Skip if we already have an entry for this date and room type
        if (!uniqueRoomDetails.has(key)) {
          // Only add if we have valid data
          if (item.count !== null && item.count !== undefined && item.rate) {
            const occupancyForItem = getOccupancyForCategory(item.roomtype_id, item.rate);
            const ratePayload = buildRatePayload(item.rate, occupancyForItem);
            uniqueRoomDetails.set(key, {
              roomtype_id: parseInt(item.roomtype_id, 10),
              rate: JSON.stringify(ratePayload),
              count: parseInt(item.count, 10),
              date: item.date
            });
          }
        }
      });

      // Format data for setHotelInventory API
      const formattedData = {
        hotel_id: parseInt(hotelId, 10),
        token: getDecryptedToken(),
        room_details: Array.from(uniqueRoomDetails.values())
      };
      
      const response = await axios.post(
        "https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/setHotelInventory",
        formattedData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data && response.data.status) {
        successToast("Inventory updated successfully!");
        // Refresh the data instead of reloading the page
        fetchHotelDetails(formatDate(currentDate));
      } else {
        const errorMessage = response.data?.message || "Failed to update inventory";
        errorToast(errorMessage);
      }
    } catch (error) {
      // Show error toast with API response message
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || "An error occurred while saving inventory data";
      errorToast(errorMessage);
    } finally {
      setIsSavingInventory(false);
    }
  };

  // Handle Bulk Update Option Change
  const handleOptionChange = (option) => {
    setBulkUpdateOption(option);
    setFormData((prev) => ({
      ...prev,
      startDate: formatDate(new Date()),
      endDate: formatDate(addMonths(new Date(), 1)),
      roomTypeId: "",
      count: "",
      occupancy: "",
      rates: {},
      roomCounts: {},
    }));
  };

  // Max end date: 2 years from today
  const bulkEndDateMax = addYears(new Date(), 2);

  // Handle Form Input Changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle Occupancy Input Change
  const handleOccupancyChange = (e) => {
    const value = e.target.value;
    if (!isNaN(value) && parseInt(value, 10) > 0) {
      setFormData((prev) => ({
        ...prev,
        occupancy: value,
        rates: {}, // Reset rates when occupancy changes
      }));
    }
  };

  // Daywise Rate Functions
  const handleDaywiseDialogOpen = () => {
    // Initialize room types with current hotel data
    const availableRoomTypes = getAvailableRoomTypes();
    
    if (availableRoomTypes.length === 0) {
      errorToast("No room types available. Please add inventory first.");
      return;
    }

    setDaywiseValidationErrors({});
    
    setDaywiseFormData(prev => ({
      ...prev,
      startDate: formatDate(new Date()),
      endDate: formatDate(addMonths(new Date(), 1)),
      selectedRoomTypes: [],
      roomTypes: availableRoomTypes
    }));
    setShowDaywiseDialog(true);
  };

  const handleDaywiseDialogClose = () => {
    setShowDaywiseDialog(false);
    setDaywiseValidationErrors({}); // Clear validation errors
    setDaywiseFormData({
      startDate: formatDate(new Date()),
      endDate: formatDate(addMonths(new Date(), 1)),
      selectedDays: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
      selectedRoomTypes: [],
      roomTypes: []
    });
  };

  // Helper function to get average room count for a room type
  const getAverageRoomCount = (roomTypeId, roomTypeName) => {
    const roomTypeInventory = inventoryData.filter(item => 
      item.roomtype_id === roomTypeId && 
      item.category_name.toLowerCase() === roomTypeName.toLowerCase()
    );
    
    if (roomTypeInventory.length === 0) return '';
    
    const counts = roomTypeInventory.map(item => parseInt(item.count) || 0);
    const averageCount = Math.round(counts.reduce((sum, count) => sum + count, 0) / counts.length);
    return averageCount > 0 ? averageCount : '';
  };

  // Helper function to get most common rates for a room type
  const getMostCommonRates = (roomTypeId, roomTypeName, occupancy) => {
    const roomTypeInventory = inventoryData.filter(item => 
      item.roomtype_id === roomTypeId && 
      item.category_name.toLowerCase() === roomTypeName.toLowerCase()
    );
    
    const rates = {};
    for (let i = 1; i <= occupancy; i++) {
      const rateKey = `${i}guest`;
      const rateValues = roomTypeInventory
        .map(item => item.rate?.[rateKey])
        .filter(rate => rate && rate !== '')
        .map(rate => parseInt(rate));
      
      if (rateValues.length > 0) {
        // Get the most common rate value
        const rateCounts = {};
        rateValues.forEach(rate => {
          rateCounts[rate] = (rateCounts[rate] || 0) + 1;
        });
        
        const mostCommonRate = Object.keys(rateCounts).reduce((a, b) => 
          rateCounts[a] > rateCounts[b] ? a : b
        );
        rates[rateKey] = mostCommonRate;
      } else {
        rates[rateKey] = '';
      }
    }
    
    return rates;
  };

  const handleRoomTypeSelection = (roomType) => {
    // Check if room type is already selected
    const isAlreadySelected = daywiseFormData.selectedRoomTypes.some(rt => rt.id === roomType.id);
    
    if (isAlreadySelected) {
      // Remove from selected room types
      setDaywiseFormData(prev => ({
        ...prev,
        selectedRoomTypes: prev.selectedRoomTypes.filter(rt => rt.id !== roomType.id)
      }));
      return;
    }
    
    // Get occupancy from amenities_by_category
    const categoryInfo = amenitiesByCategory.find(cat => cat.category_id === roomType.id);
    const occupancy = categoryInfo?.occupancy || 1;
    
    // Get average room count and most common rates
    const averageCount = getAverageRoomCount(roomType.id, roomType.name);
    const commonRates = getMostCommonRates(roomType.id, roomType.name, occupancy);
    
    const selectedRoomType = {
      ...roomType,
      count: averageCount,
      rate: commonRates,
      occupancy: occupancy
    };
    
    setDaywiseFormData(prev => ({
      ...prev,
      selectedRoomTypes: [...prev.selectedRoomTypes, selectedRoomType]
    }));
  };

  const handleRoomTypeRemove = (roomTypeId) => {
    setDaywiseFormData(prev => ({
      ...prev,
      selectedRoomTypes: prev.selectedRoomTypes.filter(rt => rt.id !== roomTypeId)
    }));
  };

  const handleDaySelection = (day) => {
    setDaywiseFormData(prev => ({
      ...prev,
      selectedDays: prev.selectedDays.includes(day)
        ? prev.selectedDays.filter(d => d !== day)
        : [...prev.selectedDays, day]
    }));
  };

  const handleDaywiseRoomTypeChange = (roomTypeId, field, value) => {
    setDaywiseFormData(prev => ({
      ...prev,
      selectedRoomTypes: prev.selectedRoomTypes.map(rt => 
        rt.id === roomTypeId 
          ? { ...rt, [field]: value }
          : rt
      )
    }));
  };

  const handleDaywiseRateChange = (roomTypeId, guestKey, value) => {
    // Allow free editing without validation
    setDaywiseFormData(prev => ({
      ...prev,
      selectedRoomTypes: prev.selectedRoomTypes.map(rt => 
        rt.id === roomTypeId 
          ? {
              ...rt,
              rate: {
                ...rt.rate,
                [guestKey]: value
              }
            }
          : rt
      )
    }));
    
    // Clear error for this field when user starts editing
    const errorKey = `${roomTypeId}-${guestKey}`;
    setDaywiseValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });
  };

  const handleDaywiseSubmit = async () => {
    try {
      setIsDaywiseSubmitting(true);
      setDaywiseValidationErrors({});
      
      // Validate dates first
      if (!daywiseFormData.startDate || !daywiseFormData.endDate) {
        setDaywiseValidationErrors({
          endDate: !daywiseFormData.endDate ? 'End date is required' : null
        });
        errorToast('Please select start date and end date.');
        // Scroll to date section
        if (dateSectionRef.current) {
          dateSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setIsDaywiseSubmitting(false);
        return;
      }
      
      if (daywiseFormData.selectedRoomTypes.length === 0) {
        errorToast('Please select at least one room type first.');
        setIsDaywiseSubmitting(false);
        return;
      }
      
      // Validate all room types and their rates
      const errors = {};
      let hasErrors = false;
      
      daywiseFormData.selectedRoomTypes.forEach((roomType) => {
        if (isRoomCountOverMax(roomType.count, MAX_TABTRIPS_ROOM_COUNT)) {
          errors[`${roomType.id}-count`] = `Room count cannot exceed ${MAX_TABTRIPS_ROOM_COUNT}.`;
          hasErrors = true;
        }

        if (!roomType.rate || Object.keys(roomType.rate).length === 0) {
          return;
        }

        // Get all rate entries sorted by guest number
        const rateEntries = Object.entries(roomType.rate)
          .filter(([key, val]) => val !== '' && val !== null && val !== undefined)
          .map(([key, val]) => {
            const guestNum = parseInt(key.replace('guest', ''));
            return { guestNum, guestKey: key, price: parseInt(val) || 0 };
          })
          .sort((a, b) => a.guestNum - b.guestNum);
        
        // Validate: price of N guests should be >= price of N-1 guests
        for (let i = 1; i < rateEntries.length; i++) {
          const currentEntry = rateEntries[i];
          const previousEntry = rateEntries[i - 1];
          
          if (currentEntry.price < previousEntry.price) {
            const errorKey = `${roomType.id}-${currentEntry.guestKey}`;
            errors[errorKey] = `Price for ${currentEntry.guestNum} guest${currentEntry.guestNum > 1 ? 's' : ''} cannot be lower than price for ${previousEntry.guestNum} guest${previousEntry.guestNum > 1 ? 's' : ''}`;
            hasErrors = true;
          }
        }
      });
      
      if (hasErrors) {
        setDaywiseValidationErrors(errors);
        errorToast('Please fix the validation errors before submitting.');
        setIsDaywiseSubmitting(false);
        return;
      }
      
      // Build skip_days array (days not selected)
      const allDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const skipDays = allDays.filter(day => !daywiseFormData.selectedDays.includes(day));
      
      // Format room types array
      const roomTypes = daywiseFormData.selectedRoomTypes.map(roomType => {
        // Format rates object
        const occupancy = getOccupancyForCategory(roomType.id, roomType.rate);
        const formattedRates = buildRatePayload(roomType.rate, occupancy);
        
        return {
          roomtype_id: roomType.id.toString(),
          rate: JSON.stringify(formattedRates),
          count: parseInt(roomType.count || 0),
          occupancy: occupancy
        };
      });

      const payload = {
        hotel_id: hotelId,
        start_date: daywiseFormData.startDate,
        end_date: daywiseFormData.endDate,
        token: getDecryptedToken(),
        skip_days: skipDays,
        room_types: roomTypes
      };

      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php?action=setDayWiseInventory',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.status) {
        successToast(`Daywise rates updated successfully for ${daywiseFormData.selectedRoomTypes.length} room type${daywiseFormData.selectedRoomTypes.length > 1 ? 's' : ''}!`);
        handleDaywiseDialogClose();
        // Refresh the calendar data
        fetchHotelDetails(formatDate(currentDate));
      } else {
        errorToast(response.data.message || 'Failed to update daywise rates.');
      }
    } catch (error) {
      console.error('Error updating daywise rates:', error);
      errorToast('An error occurred while updating daywise rates.');
    } finally {
      setIsDaywiseSubmitting(false);
    }
  };

  // Handle Bulk Update Form Submission
  const handleBulkUpdateSubmit = async (requestData) => {
    try {
      if (bulkUpdateOption === "roomCount") {
        for (const rt of requestData.room_types || []) {
          const c = parseInt(rt.count, 10);
          if (!Number.isNaN(c) && c > MAX_TABTRIPS_ROOM_COUNT) {
            errorToast(`Room count cannot exceed ${MAX_TABTRIPS_ROOM_COUNT}.`);
            return;
          }
        }
      } else {
        for (const [, guestRates] of Object.entries(formData.rates || {})) {
          if (!guestRates || typeof guestRates !== "object") continue;
          const entries = Object.entries(guestRates)
            .filter(([k, v]) => /^\d+guest$/i.test(k) && v !== "" && v != null && String(v).trim() !== "")
            .map(([k, v]) => ({
              guestNum: parseInt(String(k).replace(/guest/gi, ""), 10),
              price: parseInt(v, 10) || 0,
            }))
            .filter((e) => !Number.isNaN(e.guestNum))
            .sort((a, b) => a.guestNum - b.guestNum);
          for (let i = 1; i < entries.length; i++) {
            if (entries[i].price < entries[i - 1].price) {
              errorToast(
                `Price for ${entries[i].guestNum} guest${entries[i].guestNum > 1 ? "s" : ""} cannot be lower than for ${entries[i - 1].guestNum} guest${entries[i - 1].guestNum > 1 ? "s" : ""}.`
              );
              return;
            }
          }
        }
      }

      setIsSubmitting(true);
      setIsSubmitSuccess(false);

      // Send request to appropriate API

      const response = await axios.post(
        bulkUpdateOption === "roomCount"
          ? "https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/setCount"
          : "https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/setBulkRates",
        requestData
      );
      
      if (response.data.status) {
        // Handle successful response
        successToast("Bulk update successful!");
        setIsSubmitSuccess(true);
        window.location.reload();
      } else {
        // Handle error response
        errorToast(response.data.message || "Failed to perform bulk update.");
        setIsSubmitSuccess(false);
      }
    } catch (error) {
      console.error("Error performing bulk update:", error);
      errorToast("An error occurred while performing bulk update.");
      setIsSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render input fields for room count and rates with "Sold Out" indicator
  const renderRoomInputs = (roomType, date) => {
    if (!Array.isArray(inventoryData)) return null;
    
    const inventoryItem =
      inventoryData.find(
        (item) =>
          item.category_name && 
          item.category_name.toLowerCase() === roomType.toLowerCase() &&
          item.date === formatDateLocal(date)
      ) || {};
    
    const roomCount = inventoryItem.count !== undefined ? inventoryItem.count : "";
    const isSoldOut = roomCount === 0 || roomCount === "0";
    
    return (
      <Box 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '40px',
          margin: 0,
          padding: isMobile ? '1px 0' : '3px 0'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <BedIcon sx={{ color: '#FF7700', fontSize: '1.2rem' }} />
          <TextField
            type="number"
            placeholder={isMobile ? "" : "Room Count"}
            value={roomCount}
            onChange={(e) => handleRoomCountChange(formatDateLocal(date), roomType, e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              sx: {
                width: isMobile ? "40px" : "65px", 
                height: isMobile ? "30px" : "34px",
                '& input': {
                  textAlign: 'center',
                  padding: isMobile ? '4px 0' : '6px 2px',
                  fontWeight: 500,
                  color: isSoldOut ? '#ff3333' : 'inherit',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                },
                border: isSoldOut ? '1px solid #ff3333' : undefined,
                backgroundColor: isSoldOut ? 'rgba(255, 51, 51, 0.05)' : 'rgba(255, 246, 237, 0.5)',
              },
            }}
          />
        </Box>
        {isSoldOut && !isMobile && (
          <SoldOutBadge label="SOLD OUT" size="small" />
        )}
        {isSoldOut && isMobile && (
          <Typography variant="caption" sx={{ color: '#ff3333', fontSize: '0.6rem', fontWeight: 'bold' }}>
            SOLD
          </Typography>
        )}
      </Box>
    );
  };

  // Render rate input for guest rates (shows empty inputs when no inventory yet)
  const renderRateInputs = (roomType, date, rateKey, inventoryItem) => {
    const rate = inventoryItem?.rate ?? {};
    const guestNum = parseInt(rateKey.replace('guest', ''), 10) || 1;
    let label = null;
    let icon = <PersonIcon sx={{ fontSize: 16, color: '#888' }} />;
    if (rateKey === 'ExtraAdult') {
      label = 'Extra';
      icon = <GroupAddIcon sx={{ fontSize: 16, color: '#888' }} />;
    } else if (rateKey === 'ExtraChild') {
      label = 'Child';
      icon = <ChildCareIcon sx={{ fontSize: 16, color: '#888' }} />;
    } else {
      label = guestNum;
    }
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '40px',
        gap: 0.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#888', minWidth: '20px' }}>
            {label}
          </Typography>
          {icon}
        </Box>
        <TextField
          type="number"
          size="small"
          value={rate?.[rateKey] ?? ""}
          onChange={(e) => handleRateChange(
            formatDateLocal(date),
            roomType,
            rateKey,
            e.target.value
          )}
          variant="outlined"
          InputProps={{
            sx: {
              width: isMobile ? "40px" : "65px",
              height: "34px",
              '& input': {
                textAlign: 'center',
                padding: '6px 2px',
                fontWeight: 500,
                fontSize: '0.9rem',
              },
              backgroundColor: 'rgba(255, 246, 237, 0.3)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 119, 0, 0.05)',
              },
              '&:focus-within': {
                backgroundColor: 'rgba(255, 119, 0, 0.08)',
              }
            },
          }}
        />
      </Box>
    );
  };

  // Get the maximum number of guests dynamically
  const getMaxGuests = () => {
    if (!Array.isArray(inventoryData)) return 0;
    
    let maxGuests = 0;
    inventoryData.forEach((item) => {
      try {
        if (item.rate && typeof item.rate === 'object') {
          const rateKeys = Object.keys(item.rate);
          if (rateKeys.length > 0) {
            const maxGuestForItem = Math.max(...rateKeys.map((key) => parseInt(key.replace("guest", ""), 10) || 0));
            maxGuests = Math.max(maxGuests, maxGuestForItem);
          }
        }
      } catch (error) {
        console.error("Error processing guest count:", item, error);
      }
    });
    return maxGuests;
  };

  const maxGuests = getMaxGuests();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const rowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.3 }
    }
  };

  // Animation variants for page transitions
  const pageTransitionVariants = {
    initial: { opacity: 0, x: 10 },
    animate: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: { 
      opacity: 0, 
      x: -10,
      transition: { duration: 0.2 }
    }
  };

  // Function to handle date changes with animation
  const handleDateChange = (newDate, type) => {
    // First animate out
    document.querySelector('.table-container')?.classList.add('fade-out');
    
    // Then update date after a short delay
    setTimeout(() => {
      if (type === 'prev') {
        handleLeftArrowClick();
      } else if (type === 'next') {
        handleRightArrowClick();
      } else if (type === 'calendar') {
        handleCalendarDateChange(newDate);
      }
      
      // Finally animate back in
      setTimeout(() => {
        document.querySelector('.table-container')?.classList.remove('fade-out');
        document.querySelector('.table-container')?.classList.add('fade-in');
        
        // Remove the animation class after it completes
        setTimeout(() => {
          document.querySelector('.table-container')?.classList.remove('fade-in');
        }, 300);
      }, 50);
    }, 150);
  };

  // Update the getVisibleDates function to only show 1 day on mobile, preventing horizontal scroll
  const getVisibleDates = () => {
    if (isMobile) {
      // Show only one day on mobile to prevent horizontal scrolling
      return [dates[selectedDateIndex]];
    } else if (isTablet) {
      return dates.slice(selectedDateIndex, selectedDateIndex + 3);
    }
    return dates;
  };

  // Handle date selection
  const handleDateSelect = (index) => {
    setSelectedDateIndex(index);
  };

  // Navigation functions for mobile/tablet
  const handlePrevDate = () => {
    setSelectedDateIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextDate = () => {
    setSelectedDateIndex((prev) => Math.min(dates.length - 1, prev + 1));
  };

  const visibleDates = getVisibleDates();

  // Helper function to check if a room type is active
  const isRoomTypeActive = (roomTypeId) => {
    const roomTypeInfo = roomTypesData.find(rt => rt.room_type_category_id === roomTypeId);
    return roomTypeInfo ? roomTypeInfo.isActive === 1 : true; // Default to true if not found
  };

  // Get available room types from amenities_by_category (all room types); fallback to inventory if no categories
  const getAvailableRoomTypes = () => {
    if (Array.isArray(amenitiesByCategory) && amenitiesByCategory.length > 0) {
      return amenitiesByCategory
        .filter((cat) => isRoomTypeActive(cat.category_id))
        .map((cat) => {
          const displayName = formatCategoryDisplayName(cat.category_name);
          const name = displayName ? displayName.charAt(0).toUpperCase() + displayName.slice(1) : (cat.category_name || '');
          return { id: cat.category_id, name };
        });
    }
    if (!hotelDetails?.inventory || !Array.isArray(hotelDetails.inventory)) return [];
    const roomTypeMap = new Map();
    hotelDetails.inventory.forEach((item) => {
      if (item.roomtype_id && item.category_name && !roomTypeMap.has(item.roomtype_id) && isRoomTypeActive(item.roomtype_id)) {
        roomTypeMap.set(item.roomtype_id, {
          id: item.roomtype_id,
          name: item.category_name.charAt(0).toUpperCase() + item.category_name.slice(1)
        });
      }
    });
    return Array.from(roomTypeMap.values());
  };

  // Get filtered room types for calendar display from amenities_by_category (all room types); fallback to inventory
  const getFilteredRoomTypes = () => {
    if (Array.isArray(amenitiesByCategory) && amenitiesByCategory.length > 0) {
      return amenitiesByCategory
        .filter((cat) => isRoomTypeActive(cat.category_id))
        .map((cat) => formatCategoryDisplayName(cat.category_name))
        .filter(Boolean);
    }
    if (!hotelDetails?.inventory || !Array.isArray(hotelDetails.inventory)) return [];
    const unique = Array.from(new Set(hotelDetails.inventory.map((item) => item.category_name).filter(Boolean)));
    return unique.filter((roomType) => {
      const roomTypeData = hotelDetails.inventory.find((item) => item.category_name === roomType);
      return roomTypeData && isRoomTypeActive(roomTypeData.roomtype_id);
    });
  };

  // Function to check if a date is today
  const isDateToday = (date) => {
    return isSameDay(new Date(date), new Date());
  };
  
  useEffect(() => {
    // Removed debug logs
  }, []);

  useEffect(() => {
    // Removed debug logs
  }, [hotelId]);

  // Listen for sidebar button click events for extra debugging
  useEffect(() => {
    const handler = () => {
      // Removed debug logs
    };
    window.addEventListener('sidebarRoomRatesCalendarClicked', handler);
    return () => window.removeEventListener('sidebarRoomRatesCalendarClicked', handler);
  }, [hotelId, hotels]);

  // In the main useEffect for hotel selection, ensure default selection and redirect
  useEffect(() => {
    if (!hotelId && hotels && hotels.length > 0) {
      const firstHotel = hotels[0];
      localStorage.setItem("selectedHotelId", firstHotel.hotel_id);
      localStorage.setItem("selectedHotelName", firstHotel.hotel_name);
      if (firstHotel.host_id) {
        localStorage.setItem("selectedHostId", firstHotel.host_id);
      }
      navigate(`/room-rates-calendar/${firstHotel.hotel_id}`, { replace: true });
    }
  }, [hotelId, hotels, navigate]);

  // useEffect(() => {
  // }, [hotelDetails, inventoryData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ResponsiveContainer
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <StyledPaper>
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
              }
              @keyframes fadeOut {
                from { opacity: 1; transform: translateY(0); }
                to { opacity: 0; transform: translateY(-10px); }
              }
              .fade-in {
                animation: fadeIn 0.3s forwards;
              }
              .fade-out {
                animation: fadeOut 0.2s forwards;
              }
              .date-header {
                background-color: rgba(255, 235, 215, 0.5);
                transition: all 0.3s ease;
              }
              .date-header:hover {
                background-color: rgba(255, 119, 0, 0.1);
              }
              .date-header.today {
                background-color: rgba(255, 119, 0, 0.1);
              }
              .date-header.selected {
                background-color: rgba(255, 119, 0, 0.2);
                border-bottom: 2px solid #FF7700 !important;
              }
              .sold-out-cell {
                background-color: rgba(255, 51, 51, 0.05);
                transition: all 0.3s ease;
              }
              .sold-out-cell:hover {
                background-color: rgba(255, 51, 51, 0.1);
              }
              .no-scroll {
                overflow-x: hidden !important;
                width: 100%;
              }
              @media (max-width: 600px) {
                .MuiTableCell-root {
                  padding: 4px 0px !important;
                }
                .MuiTableRow-root {
                  padding: 0 !important;
                }
                .MuiOutlinedInput-notchedOutline {
                  border-width: 1px !important;
                }
                input[type="number"]::-webkit-inner-spin-button, 
                input[type="number"]::-webkit-outer-spin-button { 
                  -webkit-appearance: none;
                  margin: 0;
                }
                input[type="number"] {
                  -moz-appearance: textfield;
                }
                .MuiInputBase-input {
                  padding: 4px !important;
                }
              }
            `}
          </style>
          {waitingForHotelSelection ? (
            <LoadingContainer>
              <CircularProgress sx={{ color: '#FF7700', mb: 2 }} />
              <Typography variant="body1" color="textSecondary">
                Loading property information...
              </Typography>
            </LoadingContainer>
          ) : loading ? (
            <LoadingContainer>
              <CircularProgress sx={{ color: '#FF7700', mb: 2 }} />
              <Typography variant="body1" color="textSecondary">
                Loading room rates and inventory...
              </Typography>
            </LoadingContainer>
          ) : (
            <>
          <HeaderContainer>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StyledArrowButton 
                    onClick={() => handleDateChange(null, 'prev')} 
                    size="small"
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </StyledArrowButton>
              <StyledDatePicker
                value={currentDate}
                format="dd/MM/yyyy"
                    onChange={(newDate) => handleDateChange(format(newDate, 'yyyy-MM-dd'), 'calendar')}
                renderInput={(params) => <TextField {...params} size={isMobile ? "small" : "medium"} />}
              />
                  <StyledArrowButton 
                    onClick={() => handleDateChange(null, 'next')} 
                    size="small"
                  >
                    <ChevronRightIcon fontSize="small" />
                  </StyledArrowButton>
            </Box>

            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              component={motion.h2}
                  sx={{ 
                    fontWeight: 600, 
                    color: '#FF7700',
                    textAlign: isMobile ? 'center' : 'left',
                    width: isMobile ? '100%' : 'auto'
                  }}
            >
              Room Rates Calendar
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <ActionButton
                variant="outlined"
                onClick={handleDaywiseDialogOpen}
                startIcon={<EventIcon />}
                sx={{ 
                  borderColor: '#FF7700',
                  color: '#FF7700',
                  '&:hover': {
                    borderColor: '#E56900',
                    backgroundColor: 'rgba(255, 119, 0, 0.05)',
                  }
                }}
              >
                Daywise Rates
              </ActionButton>
              <ActionButton
                variant="contained"
                    onClick={() => setShowBulkUpdate(!showBulkUpdate)}
                    startIcon={showBulkUpdate ? <ChevronLeftIcon /> : <UpdateIcon />}
              >
                    {showBulkUpdate ? 'Back to Calendar' : 'Bulk Update'}
              </ActionButton>
            </Box>
          </HeaderContainer>

              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row', 
                mb: 2.5 
              }}>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  sx={{ 
                    fontWeight: 600,
                    mb: isMobile ? 1 : 0
                  }}
                >
            {hotelDetails?.hotel_name || 'Loading...'}
          </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  padding: '4px 12px',
                  backgroundColor: 'rgba(255, 119, 0, 0.1)',
                  borderRadius: '16px',
                  alignSelf: isMobile ? 'flex-start' : 'center'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#FF7700' }}>
                    {isMobile ? 'ID:' : 'Hotel ID:'} {hotelId}
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  padding: '4px 12px',
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '16px',
                  alignSelf: isMobile ? 'flex-start' : 'center'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    Currency: {currencyLabel}
                  </Typography>
                </Box>
              </Box>

          {!showBulkUpdate ? (
            <>
              {isMobile && (
                <MobileDateSelector>
                      <StyledArrowButton 
                        onClick={() => handlePrevDate()}
                    disabled={selectedDateIndex === 0}
                        size="small"
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </StyledArrowButton>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        {format(dates[selectedDateIndex], 'EEE, MMM dd, yyyy')}
                  </Typography>
                      <StyledArrowButton 
                        onClick={() => handleNextDate()}
                    disabled={selectedDateIndex >= dates.length - 1}
                        size="small"
                  >
                        <ChevronRightIcon fontSize="small" />
                      </StyledArrowButton>
                </MobileDateSelector>
              )}

                  {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <CircularProgress size={40} sx={{ color: '#FF7700' }} />
                      <Typography sx={{ mt: 2 }}>Loading inventory data...</Typography>
                    </Box>
                  ) : (
                    <motion.div
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      variants={pageTransitionVariants}
                      className="table-container"
                    >
                      <StyledTableContainer className={isMobile ? "no-scroll" : "allow-scroll-x"}>
                <ResponsiveTable>
                  <TableHead>
                            <TableRow sx={{ 
                              backgroundColor: 'rgba(255, 235, 215, 0.3)',
                              '& th': { fontWeight: 600 }
                            }}>
                      <TableCell 
                        sx={{ 
                          position: 'sticky',
                          left: 0,
                                  backgroundColor: theme.palette.background.paper,
                          zIndex: 2,
                                  minWidth: '150px'
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Room Type
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          Rates in {currencyLabel}
                        </Typography>
                      </TableCell>
                              {visibleDates.map((date, index) => (
                                  <TableCell
                                  key={formatDate(date)}
                          align="center"
                          sx={{
                                    minWidth: isMobile ? '80px' : '120px',
                                    position: 'relative',
                                    backgroundColor: isDateToday(date) ? 'rgba(255, 119, 0, 0.05)' : 'inherit'
                          }}
                        >
                                  <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      {format(date, 'EEE')}
                          </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {format(date, 'MMM d')}
                                      </Typography>
                                  </Box>
                                  </TableCell>
                              ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <AnimatePresence>
                      {getFilteredRoomTypes().length > 0 ? (
                        getFilteredRoomTypes().map((roomType) => (
                          <React.Fragment key={roomType}>
                            <AnimatedTableRow
                              variants={rowVariants}
                              initial="hidden"
                              animate="visible"
                            >
                              <TableCell
                                sx={{
                                  position: 'sticky',
                                  left: 0,
                                          backgroundColor: expandedRooms[roomType] 
                                            ? 'rgba(255, 119, 0, 0.05)' 
                                            : theme.palette.background.paper,
                                  zIndex: 1,
                                          borderLeft: expandedRooms[roomType] 
                                            ? '3px solid #FF7700' 
                                            : undefined,
                                          fontWeight: 600,
                                          color: expandedRooms[roomType] ? '#FF7700' : undefined,
                                          minWidth: '150px'
                                }}
                              >
                                        <RoomTypeButton
                                  onClick={() => toggleRoomExpansion(roomType)}
                                  size={isMobile ? "small" : "medium"}
                                          color="inherit"
                                  endIcon={expandedRooms[roomType] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  fullWidth
                                          data-is-expanded={expandedRooms[roomType] ? "true" : "false"}
                                >
                                          {roomType.toUpperCase()}
                                        </RoomTypeButton>
                              </TableCell>
                                      {visibleDates.map((date) => {
                                        const dateKey = formatDate(date);
                                        const isTodayColumn = isDateToday(date);
                                        const inventoryItem = inventoryData.find(
                                          (item) =>
                                            item.category_name.toLowerCase() === roomType.toLowerCase() &&
                                            item.date === formatDateLocal(date)
                                        ) || {};
                                        const effectiveRoomTypeId = inventoryItem.roomtype_id ?? getRoomTypeIdByDisplayName(roomType);
                                        const effectiveInventoryItem = effectiveRoomTypeId != null
                                          ? { ...inventoryItem, roomtype_id: effectiveRoomTypeId, rate: inventoryItem.rate || {} }
                                          : inventoryItem;
                                        const roomCount = inventoryItem.count !== undefined ? inventoryItem.count : "";
                                        const isSoldOut = roomCount === 0 || roomCount === "0";
                                        
                                        return (
                                <TableCell
                                            key={`${roomType}-${dateKey}`}
                                  align="center"
                                  sx={{
                                              backgroundColor: isTodayColumn 
                                                ? 'rgba(255, 119, 0, 0.05)' 
                                                : 'inherit',
                                              minWidth: isMobile ? '80px' : '120px',
                                              position: 'relative',
                                              padding: '8px 4px',
                                              verticalAlign: 'top'
                                            }}
                                  >
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center',
                                      width: '100%'
                                    }}>
                                      {/* Room Count Input - Centered */}
                                      <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        width: '100%',
                                        mb: expandedRooms[roomType] ? 1 : 0
                                      }}>
                                        {renderRoomInputs(roomType, date)}
                                      </Box>
                                      
                                      {/* Rate Inputs - Centered and Aligned */}
                                      {expandedRooms[roomType] && effectiveRoomTypeId != null && (
                                        <Box sx={{ 
                                          display: 'flex', 
                                          flexDirection: 'column', 
                                          alignItems: 'center',
                                          width: '100%',
                                          gap: 0.5
                                        }}>
                                          {(() => {
                                            const occupancyForItem = getOccupancyForCategory(
                                              effectiveRoomTypeId,
                                              effectiveInventoryItem.rate
                                            );
                                            const visibleRates = Object.entries(
                                              sanitizeRatesForOccupancy(effectiveInventoryItem.rate, occupancyForItem)
                                            ).sort(
                                              ([keyA], [keyB]) =>
                                                parseInt(keyA.replace('guest', ''), 10) -
                                                parseInt(keyB.replace('guest', ''), 10)
                                            );

                                            return visibleRates.map(([rateKey]) => (
                                              <Box key={`${dateKey}-${rateKey}`} sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'center', 
                                                width: '100%'
                                              }}>
                                                {renderRateInputs(roomType, date, rateKey, effectiveInventoryItem)}
                                              </Box>
                                            ));
                                          })()}
                                        </Box>
                                      )}
                                    </Box>
                                          </TableCell>
                                            );
                                          })}
                                    </AnimatedTableRow>
                          </React.Fragment>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={visibleDates.length + 1} align="center">
                                    <Box sx={{ p: 4 }}>
                                      <Typography color="textSecondary" variant="body1">
                                        No inventory data available
                                      </Typography>
                                      <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                        Please use the Bulk Update option to add inventory
                                      </Typography>
                                    </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </AnimatePresence>
                  </TableBody>
                </ResponsiveTable>
              </StyledTableContainer>
                    </motion.div>
                  )}

                  {/* Date navigation for non-mobile */}
              {!isMobile && dates.length > (isTablet ? 3 : dates.length) && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
                      <StyledArrowButton
                        onClick={() => handlePrevDate()}
                    disabled={selectedDateIndex === 0}
                        size="small"
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </StyledArrowButton>
                      <StyledArrowButton
                        onClick={() => handleNextDate()}
                    disabled={selectedDateIndex >= dates.length - (isTablet ? 3 : dates.length)}
                        size="small"
                  >
                        <ChevronRightIcon fontSize="small" />
                      </StyledArrowButton>
                </Box>
              )}

                  {/* Save Button */}
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <motion.div
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <ActionButton
                        variant="contained"
                        onClick={saveInventoryData}
                        disabled={isSavingInventory}
                        startIcon={isSavingInventory ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{ 
                          minWidth: isMobile ? '100%' : '220px',
                          py: 1.2
                        }}
                      >
                        {isSavingInventory ? 'Saving...' : 'Save Changes'}
                      </ActionButton>
                    </motion.div>
                    
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                      All changes will be saved for the displayed dates
                    </Typography>
                  </Box>
            </>
          ) : (
            <BulkUpdateForm>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 2,
                  borderBottom: '1px solid rgba(200,200,200,0.15)',
                  pb: 1.5,
                  mb: 2,
                  background: 'rgba(255,255,255,0.18)',
                  boxShadow: '0 4px 24px 0 rgba(31, 38, 135, 0.07)',
                  backdropFilter: 'blur(6px)',
                  borderRadius: '12px',
                  transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <UpdateIcon sx={{ color: 'rgba(80,80,80,0.85)', fontSize: '1.5rem' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#222', letterSpacing: 0.5 }}>
                      Bulk Update
                    </Typography>
                    {bulkUpdateOption === 'rate' && (
                      <Typography variant="caption" color="text.secondary">
                        Rates in {currencyLabel}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant={bulkUpdateOption === "roomCount" ? "contained" : "outlined"}
                    onClick={() => handleOptionChange("roomCount")}
                    startIcon={<BedIcon />}
                    size="small"
                    sx={{
                      color: bulkUpdateOption === "roomCount" ? '#fff' : '#222',
                      background: bulkUpdateOption === "roomCount" ? 'rgba(30,32,40,0.85)' : 'transparent',
                      border: bulkUpdateOption === "roomCount" ? 'none' : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      minWidth: 120,
                      boxShadow: 'none',
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: 'rgba(30,32,40,1)',
                        color: '#fff',
                      },
                    }}
                  >
                    Room Count
                  </Button>
                  <Button
                    variant={bulkUpdateOption === "rate" ? "contained" : "outlined"}
                    onClick={() => handleOptionChange("rate")}
                    startIcon={<AttachMoneyIcon />}
                    size="small"
                    sx={{
                      color: bulkUpdateOption === "rate" ? '#fff' : '#222',
                      background: bulkUpdateOption === "rate" ? 'rgba(30,32,40,0.85)' : 'transparent',
                      border: bulkUpdateOption === "rate" ? 'none' : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      minWidth: 120,
                      boxShadow: 'none',
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: 'rgba(30,32,40,1)',
                        color: '#fff',
                      },
                    }}
                  >
                    Rates
                  </Button>
                </Box>
              </Box>
              <StyledFormSection>
                <Grid container spacing={3}>
                  {/* Date range * - aligned Start & End */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 1.5 }}>
                      Date range *
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="Start date"
                            value={formData.startDate ? new Date(formData.startDate) : null}
                            minDate={new Date()}
                            onChange={(newValue) => {
                              setFormData((prev) => ({
                                ...prev,
                                startDate: formatDate(newValue),
                                ...(prev.endDate && new Date(prev.endDate) < newValue ? { endDate: '' } : {}),
                              }));
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                size="small"
                                sx={{
                                  background: 'rgba(255,255,255,0.85)',
                                  borderRadius: '10px',
                                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="End date"
                            value={formData.endDate ? new Date(formData.endDate) : null}
                            minDate={formData.startDate ? new Date(formData.startDate) : new Date()}
                            maxDate={bulkEndDateMax}
                            onChange={(newValue) => {
                              setFormData((prev) => ({ ...prev, endDate: formatDate(newValue) }));
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                size="small"
                                helperText="Max 2 years from today"
                                sx={{
                                  background: 'rgba(255,255,255,0.85)',
                                  borderRadius: '10px',
                                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* All room types with inputs (bulk count / bulk rates) */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 1.5 }}>
                      Room types
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      {bulkUpdateOption === 'roomCount'
                        ? 'Enter counts for each room type you want to update (leave blank to skip).'
                        : `Enter rates in ${currencyLabel} per occupancy for each room type you want to update (leave blank to skip).`}
                    </Typography>
                    {getAvailableRoomTypes().length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {getAvailableRoomTypes().map((rt) => {
                          const roomTypeId = rt.id;
                          const categoryInfo = amenitiesByCategory.find((c) => c.category_id === roomTypeId);
                          const occupancy = categoryInfo?.occupancy ?? getOccupancyForCategory(roomTypeId, {});
                          return (
                            <Box
                              key={roomTypeId}
                              sx={{
                                p: 2,
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 119, 0, 0.2)',
                                background: 'rgba(255,255,255,0.5)',
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'center' },
                                gap: 2,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#FF7700', minWidth: { sm: 140 }, flex: { sm: '0 0 auto' } }}>
                                {rt.name}
                              </Typography>
                              {bulkUpdateOption === 'roomCount' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: '1 1 auto' }}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    placeholder="Count"
                                    inputProps={{ min: 0 }}
                                    value={formData.roomCounts?.[roomTypeId] ?? ''}
                                    onChange={(e) => {
                                      setFormData((prev) => ({
                                        ...prev,
                                        roomCounts: { ...prev.roomCounts, [roomTypeId]: e.target.value },
                                      }));
                                    }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <BedIcon sx={{ color: '#FF7700', fontSize: '1.2rem' }} />
                                        </InputAdornment>
                                      ),
                                      sx: {
                                        width: { xs: '100%', sm: 120 },
                                        maxWidth: '100%',
                                        background: 'rgba(255,255,255,0.9)',
                                        borderRadius: '8px',
                                        '& input': { textAlign: 'center', fontWeight: 600 },
                                      },
                                    }}
                                  />
                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    rooms
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', flex: '1 1 auto' }}>
                                  {Array.from({ length: occupancy }, (_, i) => {
                                    const guestNum = i + 1;
                                    const rateKey = `${guestNum}guest`;
                                    const label = `${guestNum} Guest${guestNum > 1 ? 's' : ''}`;
                                    const currentRates = formData.rates?.[roomTypeId] || {};
                                    return (
                                      <TextField
                                        key={rateKey}
                                        type="number"
                                        size="small"
                                        placeholder={label}
                                        inputProps={{ min: 0 }}
                                        value={currentRates[rateKey] ?? ''}
                                        onChange={(e) => {
                                          setFormData((prev) => ({
                                            ...prev,
                                            rates: {
                                              ...prev.rates,
                                              [roomTypeId]: {
                                                ...(prev.rates?.[roomTypeId] || {}),
                                                [rateKey]: e.target.value,
                                              },
                                            },
                                          }));
                                        }}
                                        InputProps={{
                                          sx: {
                                            width: { xs: '100%', sm: 110 },
                                            maxWidth: '100%',
                                            background: 'rgba(255,255,255,0.9)',
                                            borderRadius: '8px',
                                            '& input': { textAlign: 'center', fontWeight: 600 },
                                          },
                                        }}
                                      />
                                    );
                                  })}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(255,119,0,0.06)', borderRadius: 2, border: '1px dashed rgba(255,119,0,0.25)' }}>
                        <Typography variant="body2" color="text.secondary">
                          No room types available.
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
                {/* Submit Button */}
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                  <ActionButton
                    variant="contained"
                    onClick={() => {
                      const requestData = {
                        hotel_id: hotelId,
                        token: getDecryptedToken(),
                        start_date: formData.startDate,
                        end_date: formData.endDate,
                        room_types: bulkUpdateOption === "roomCount"
                          ? Object.entries(formData.roomCounts || {})
                              .filter(([_, count]) => count && count !== '')
                              .map(([roomTypeId, count]) => ({
                                roomtype_id: roomTypeId,
                                count: parseInt(count)
                              }))
                          : Object.entries(formData.rates || {})
                              .filter(([_, rates]) => Object.values(rates).some(rate => rate && rate !== ''))
                              .map(([roomTypeId, guestRates]) => {
                                const occupancy = getOccupancyForCategory(roomTypeId, guestRates);
                                const formattedRates = buildRatePayload(guestRates, occupancy);
                                
                                return {
                                  roomtype_id: roomTypeId,
                                  rate: JSON.stringify(formattedRates),
                                  occupancy: occupancy
                                };
                              })
                      };
                      handleBulkUpdateSubmit(requestData);
                    }}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : (bulkUpdateOption === "roomCount" ? <BedIcon /> : <AttachMoneyIcon />)}
                    disabled={
                      isSubmitting ||
                      isSubmitSuccess ||
                      !formData.startDate ||
                      !formData.endDate ||
                      getAvailableRoomTypes().length === 0 ||
                      (bulkUpdateOption === "roomCount"
                        ? !Object.values(formData.roomCounts || {}).some((count) => count !== undefined && count !== null && String(count).trim() !== '')
                        : !Object.values(formData.rates || {}).some((rates) => rates && Object.values(rates).some((rate) => rate !== undefined && rate !== null && String(rate).trim() !== '')))
                    }
                    sx={{
                      px: 4,
                      py: 1.2,
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderRadius: '10px',
                      background: isSubmitting || isSubmitSuccess ? '#4CAF50' : 'rgba(30,32,40,0.85)',
                      color: '#fff',
                      boxShadow: '0 2px 8px #e0e0e0',
                      letterSpacing: 0.5,
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: isSubmitting || isSubmitSuccess ? '#45a049' : 'rgba(30,32,40,1)',
                        color: '#fff',
                      },
                      ...(!isSubmitting && isSubmitSuccess && {
                        background: '#4CAF50',
                        '&:hover': {
                          background: '#45a049',
                        },
                      }),
                    }}
                  >
                    {isSubmitting
                      ? 'Submitting...'
                      : isSubmitSuccess
                        ? 'Updated Successfully'
                        : bulkUpdateOption === "roomCount"
                          ? 'Set Inventory'
                          : 'Set Rates'}
                  </ActionButton>
                </Box>
              </StyledFormSection>
            </BulkUpdateForm>
              )}
            </>
          )}

          {/* Daywise Rate Dialog */}
          <StyledDialog
            open={showDaywiseDialog}
            onClose={handleDaywiseDialogClose}
            maxWidth="md"
            fullWidth
          >
            <DialogHeader>
              <Box>
                <DialogTitleStyled>
                  <EventIcon />
                  Daywise Rate Management
                </DialogTitleStyled>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4.5 }}>
                  Rates in {currencyLabel}
                </Typography>
              </Box>
              <IconButton
                onClick={handleDaywiseDialogClose}
                sx={{ color: '#FF7700' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogHeader>

            <DialogContentStyled>
              {/* Date Range Selection */}
              <Box ref={dateSectionRef} sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#FF7700' }}>
                  Select Date Range
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Start Date"
                        value={daywiseFormData.startDate ? new Date(daywiseFormData.startDate) : null}
                        minDate={new Date()}
                        inputFormat="dd-MMM-yy"
                        disableMaskedInput
                        onChange={(newValue) => {
                          setDaywiseFormData(prev => ({
                            ...prev,
                            startDate: formatDate(newValue)
                          }));
                        }}
                        renderInput={(params) => (
                          <StyledInputField
                            {...params}
                            fullWidth
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <DateRangeIcon sx={{ color: '#FF7700' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="End Date"
                          value={daywiseFormData.endDate ? new Date(daywiseFormData.endDate) : null}
                          minDate={daywiseFormData.startDate ? new Date(daywiseFormData.startDate) : new Date()}
                          maxDate={addYears(new Date(), 2)}
                          inputFormat="dd-MMM-yy"
                          disableMaskedInput
                          onChange={(newValue) => {
                            setDaywiseFormData(prev => ({
                              ...prev,
                              endDate: formatDate(newValue)
                            }));
                            // Clear end date error when user selects a date
                            setDaywiseValidationErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.endDate;
                              return newErrors;
                            });
                          }}
                          renderInput={(params) => {
                            const hasError = daywiseValidationErrors.endDate;
                            return (
                              <StyledInputField
                                {...params}
                                fullWidth
                                size="small"
                                error={hasError}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: hasError ? '#d32f2f' : undefined
                                    }
                                  }
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <DateRangeIcon sx={{ color: hasError ? '#d32f2f' : '#FF7700' }} />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            );
                          }}
                        />
                      </LocalizationProvider>
                      {daywiseValidationErrors.endDate && (
                        <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                          {daywiseValidationErrors.endDate}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                        Max 2 years from today
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Day Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#FF7700' }}>
                  Select Days
                </Typography>
                <DaySelectionContainer>
                  {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => (
                    <DayChip
                      key={day}
                      label={day.charAt(0).toUpperCase() + day.slice(1)}
                      selected={daywiseFormData.selectedDays.includes(day)}
                      onClick={() => handleDaySelection(day)}
                      clickable
                    />
                  ))}
                </DaySelectionContainer>
              </Box>

              {/* Room Type Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#FF7700' }}>
                  Select Room Types
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {daywiseFormData.roomTypes.map((roomType) => {
                    const isSelected = daywiseFormData.selectedRoomTypes.some(rt => rt.id === roomType.id);
                    return (
                      <DayChip
                        key={roomType.id}
                        label={roomType.name}
                        selected={isSelected}
                        onClick={() => handleRoomTypeSelection(roomType)}
                        clickable
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Room Configuration - Show for each selected room type */}
              {daywiseFormData.selectedRoomTypes.map((roomType) => {
                const countErr = daywiseValidationErrors[`${roomType.id}-count`];
                return (
                <Box key={roomType.id} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#FF7700' }}>
                    Configure {roomType.name}
                  </Typography>
                  
                  <RoomInputContainer>
                    {/* Room Count Row */}
                    <RoomInputRow>
                      <EmptyDiv />
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BedIcon sx={{ color: countErr ? '#d32f2f' : '#FF7700', fontSize: '1.2rem' }} />
                          <StyledInputField
                            type="number"
                            placeholder="Room Count"
                            value={roomType.count || ''}
                            onChange={(e) => handleDaywiseRoomTypeChange(roomType.id, 'count', e.target.value)}
                            size="small"
                            sx={{
                              width: '120px',
                              '& .MuiOutlinedInput-root fieldset': { borderColor: countErr ? '#d32f2f' : undefined },
                            }}
                          />
                        </Box>
                        {countErr && (
                          <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '0.7rem', ml: '28px' }}>
                            {countErr}
                          </Typography>
                        )}
                      </Box>
                    </RoomInputRow>

                    {/* Rate Inputs Row */}
                    <RoomInputRow>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '48px' }}>
                        <PersonIcon sx={{ color: '#FF7700', fontSize: '1.2rem' }} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
                        {(() => {
                          const categoryInfo = amenitiesByCategory.find(cat => cat.category_id === roomType.id);
                          const occupancy = categoryInfo?.occupancy || 1;
                          
                          return Array.from({ length: occupancy }, (_, idx) => {
                            const guestNum = idx + 1;
                            const rateKey = `${guestNum}guest`;
                            const currentRate = roomType.rate?.[rateKey] || '';
                            
                            const errorKey = `${roomType.id}-${rateKey}`;
                            const hasError = daywiseValidationErrors[errorKey];
                            
                            return (
                              <Box key={rateKey} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: hasError ? '#d32f2f' : '#FF7700', minWidth: '20px' }}>
                                    {guestNum}
                                  </Typography>
                                  <StyledInputField
                                    type="number"
                                    placeholder={`${guestNum} Guest${guestNum > 1 ? 's' : ''}`}
                                    value={currentRate}
                                    onChange={(e) => handleDaywiseRateChange(roomType.id, rateKey, e.target.value)}
                                    size="small"
                                    sx={{ 
                                      width: '100px',
                                      '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                          borderColor: hasError ? '#d32f2f' : undefined
                                        }
                                      }
                                    }}
                                  />
                                </Box>
                                {hasError && (
                                  <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '0.7rem', ml: '28px', maxWidth: '150px' }}>
                                    {daywiseValidationErrors[errorKey]}
                                  </Typography>
                                )}
                              </Box>
                            );
                          });
                        })()}
                      </Box>
                    </RoomInputRow>
                  </RoomInputContainer>
                </Box>
              );
              })}
            </DialogContentStyled>

            <DialogActions sx={{ padding: 3, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
              <Button
                onClick={handleDaywiseDialogClose}
                variant="outlined"
                sx={{
                  borderColor: '#FF7700',
                  color: '#FF7700',
                  '&:hover': {
                    borderColor: '#E56900',
                    backgroundColor: 'rgba(255, 119, 0, 0.05)',
                  }
                }}
              >
                Cancel
              </Button>
              <ActionButton
                variant="contained"
                onClick={handleDaywiseSubmit}
                disabled={
                  isDaywiseSubmitting ||
                  daywiseFormData.selectedDays.length === 0 ||
                  daywiseFormData.selectedRoomTypes.length === 0 ||
                  daywiseFormData.selectedRoomTypes.some(rt => !rt.count || rt.count === '')
                }
                startIcon={isDaywiseSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              >
                {isDaywiseSubmitting ? 'Updating...' : 'Update Daywise Rates'}
              </ActionButton>
            </DialogActions>
          </StyledDialog>
        </StyledPaper>
      </ResponsiveContainer>
    </LocalizationProvider>
  );
};

export default RoomRatesCalendar;